import { useMemo, useState, type ReactNode } from 'react';
import { displayName, lifeSpan } from '../../model/people';
import { normalize, search } from '../../model/search';
import { Avatar, Modal } from './common';
import { startSync } from './SyncDialog';
import { closeDialog, focusPerson, getState, openDialog, redo, setState, setView, undo, useApp, type View } from '../store';

interface Item {
  key: string;
  label: ReactNode;
  hint?: string;
  run: () => void;
  icon?: ReactNode;
}

export function CommandPalette() {
  const tree = useApp((s) => s.tree);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);

  const commands: { label: string; keywords: string; run: () => void }[] = useMemo(() => {
    const go = (v: View) => () => setView(v);
    const list = [
      { label: 'Add a person', keywords: 'new create person', run: () => openDialog({ type: 'new-person' }) },
      { label: 'Go to Tree', keywords: 'chart family view', run: go('tree') },
      { label: 'Go to Network', keywords: 'graph friends view', run: go('network') },
      { label: 'Go to People', keywords: 'list search table view', run: go('people') },
      { label: 'Go to Relate (relationship finder)', keywords: 'how related kinship calculator', run: go('relate') },
      { label: 'Go to Groups', keywords: 'friend group found family', run: go('groups') },
      { label: 'Go to Reports', keywords: 'stats statistics charts facts gender split', run: go('reports') },
      { label: 'Go to Countdowns', keywords: 'birthdays anniversaries upcoming coming up remembrance dates', run: go('countdowns') },
      { label: 'Go to Map', keywords: 'places where born birthplaces lived homes locations world geography', run: go('map') },
      { label: 'New group', keywords: 'create friend found family household', run: () => openDialog({ type: 'edit-group', groupId: null }) },
      { label: 'Go to Media', keywords: 'photos pictures documents scans gallery recordings videos', run: go('media') },
      { label: 'Go to Sources', keywords: 'citations records evidence research bibliography', run: go('sources') },
      { label: 'Go to To-dos and suggestions', keywords: 'tasks research todo checklist suggestions what next missing gaps ideas duplicates add parents', run: go('todos') },
      { label: 'New source', keywords: 'create citation record evidence', run: () => openDialog({ type: 'edit-source', sourceId: null }) },
      { label: 'New to-do', keywords: 'create task research todo', run: () => openDialog({ type: 'edit-todo', todoId: null }) },
      { label: 'Add a file to this tree', keywords: 'import merge combine upload load ged gedcom mftpkg macfamilytree relationships', run: () => openDialog({ type: 'import', mode: 'merge' }) },
      { label: 'Open a file as a new tree', keywords: 'import open upload load ged gedcom mftpkg macfamilytree relationships', run: () => openDialog({ type: 'import', mode: 'new' }) },
      { label: 'Export / download', keywords: 'save backup gedcom json', run: () => openDialog({ type: 'export' }) },
      { label: 'Sync with another device', keywords: 'sync phone laptop computer copy transfer devices wifi pair qr', run: startSync },
      { label: 'Switch or manage trees', keywords: 'open tree new rename delete', run: () => openDialog({ type: 'trees' }) },
      { label: 'Undo', keywords: 'revert', run: undo },
      { label: 'Redo', keywords: '', run: redo },
      { label: 'Settings', keywords: 'preferences theme dark light', run: () => openDialog({ type: 'settings' }) },
      { label: 'Help and keyboard shortcuts', keywords: 'help shortcuts keys syntax', run: () => openDialog({ type: 'help' }) },
    ];
    if (tree?.homePersonId && tree.people[tree.homePersonId])
      list.splice(1, 0, { label: 'Go to home person', keywords: 'home root me', run: () => focusPerson(tree.homePersonId!, { view: 'tree' }) });
    return list;
  }, [tree]);

  const items: { group: string; items: Item[] }[] = useMemo(() => {
    const nq = normalize(q);
    const people = tree && q.trim() ? search(tree, q, { limit: 8 }) : [];
    const cmds = commands.filter((c) => !nq || normalize(c.label + ' ' + c.keywords).split(' ').some((w) => w.startsWith(nq.split(' ')[0])) && nq.split(' ').every((t) => normalize(c.label + ' ' + c.keywords).includes(t)));
    const out: { group: string; items: Item[] }[] = [];
    if (people.length) {
      const view = getState().view;
      out.push({
        group: 'People',
        items: [
          ...people.map((r) => ({
            key: r.person.id,
            label: (
              <span>
                <b>{displayName(r.person)}</b> <span className="faint small-text">{[lifeSpan(r.person), r.person.pronouns].filter(Boolean).join(' · ')}</span>
              </span>
            ),
            icon: <Avatar person={r.person} size="sm" />,
            run: () => focusPerson(r.person.id, { view: view === 'tree' || view === 'network' || view === 'people' ? view : 'tree' }),
          })),
          {
            key: 'all-results',
            label: <span>See all results for “{q.trim()}”</span>,
            run: () => {
              setState({ peopleQuery: q.trim() });
              setView('people');
            },
          },
        ],
      });
    }
    if (cmds.length) out.push({ group: 'Commands', items: cmds.slice(0, q ? 8 : 20).map((c) => ({ key: c.label, label: c.label, run: c.run })) });
    return out;
  }, [q, tree, commands]);

  const flat = items.flatMap((g) => g.items);
  const run = (i: number) => {
    const it = flat[i];
    if (!it) return;
    closeDialog();
    it.run();
  };

  let idx = -1;
  return (
    <Modal className="palette" onClose={closeDialog}>
      <input
        className="input palette-input"
        placeholder="Find a person or type a command…"
        value={q}
        autoFocus
        aria-label="Search people and commands"
        role="combobox"
        aria-expanded
        aria-controls="palette-list"
        aria-activedescendant={flat[active] ? `pal-${active}` : undefined}
        onChange={(e) => {
          setQ(e.target.value);
          setActive(0);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(flat.length - 1, a + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            run(active);
          }
        }}
      />
      <div className="palette-list" id="palette-list" role="listbox">
        {items.map((g) => (
          <div key={g.group}>
            <div className="palette-group">{g.group}</div>
            {g.items.map((it) => {
              idx++;
              const i = idx;
              return (
                <div
                  key={it.key}
                  id={`pal-${i}`}
                  role="option"
                  aria-selected={i === active}
                  className="picker-item"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => run(i)}
                >
                  {it.icon}
                  {it.label}
                </div>
              );
            })}
          </div>
        ))}
        {!flat.length && <div className="faint" style={{ padding: 16 }}>No matches.</div>}
      </div>
    </Modal>
  );
}
